import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useOverflowObserver } from '@/hooks/useOverflowObserver';
import { ClientBadge } from './ClientBadge';
import type { ClientActivity } from '@kirin/api-client/contracts/user-security-posture';
import { getIdeClientDisplayLabel } from '@/components/shared/platform';

interface ClientBadgesProps {
  clients: ClientActivity[];
  leading?: ReactNode;
  /** Class applied to the empty-state dash (e.g. indent vs. center). */
  emptyClassName?: string;
  /** Called when the "+N" overflow chip is clicked (e.g. to open details). */
  onOverflowClick?: () => void;
}

function clientKey(c: ClientActivity, i: number): string {
  return `${c.client}-${c.coverageStatus}-${i}`;
}

function Badge({ client }: { client: ClientActivity }): ReactElement {
  return (
    <ClientBadge
      client={client.client}
      coverageStatus={client.coverageStatus}
      lastActiveAt={client.lastActiveAt}
      displayName={client.displayName}
      loggedInUserEmail={client.loggedInUserEmail}
      loggedInUserMatch={client.loggedInUserMatch}
    />
  );
}

/**
 * Displays IDE client badges, showing as many as fit the available width and
 * collapsing the rest into a "+N" chip. `useOverflowObserver` measures each
 * badge's natural width once, then recomputes the fit against the live
 * container width on resize, so it adapts as the column resizes (KIR-2916).
 *
 * Single render tree (KIR-2546): each badge is mounted exactly once. The
 * observer caches natural widths the first time every badge is shown, so
 * overflow can be collapsed by simply not rendering the hidden ones — there is
 * no second, duplicate copy of every badge. The only off-flow helper is a
 * single "+N" span used to reserve the overflow chip's width.
 */
export function ClientBadges({
  clients,
  leading,
  emptyClassName,
  onOverflowClick,
}: ClientBadgesProps): ReactElement {
  // Width-cache key: only the inputs that affect badge width (the label =
  // client + displayName). coverageStatus/mismatch are color-only, so excluded.
  const signature = useMemo(
    () => clients.map(c => `${c.client} ${c.displayName ?? ''}`).join('|'),
    [clients]
  );
  const {
    containerRef,
    registerItem,
    registerChip,
    registerLeading,
    visibleCount,
  } = useOverflowObserver(clients.length, signature);

  if (clients.length === 0 && !leading) {
    return <div className={cn('text-muted-foreground', emptyClassName)}>—</div>;
  }

  const visibleClients = clients.slice(0, visibleCount);
  const hiddenClients = clients.slice(visibleCount);
  const hiddenCount = hiddenClients.length;

  return (
    <div
      ref={containerRef}
      data-testid="client-badges"
      className="relative flex items-center gap-1 w-full min-w-0 overflow-hidden"
    >
      {leading && (
        <span
          ref={registerLeading}
          className="inline-flex shrink-0 items-center"
        >
          {leading}
        </span>
      )}
      {visibleClients.map((c, i) => (
        <span
          key={clientKey(c, i)}
          ref={registerItem(i)}
          className="inline-flex shrink-0 items-center whitespace-nowrap"
        >
          <Badge client={c} />
        </span>
      ))}
      {hiddenCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="ml-auto inline-flex shrink-0 items-center rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
              aria-label={`${hiddenCount} more clients`}
              onClick={e => {
                e.stopPropagation();
                onOverflowClick?.();
              }}
            >
              +{hiddenCount}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {hiddenClients.map((c, i) => (
              <div key={clientKey(c, i)}>
                {getIdeClientDisplayLabel(c.client, c.displayName)}
              </div>
            ))}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Off-flow "+N" span the observer measures to reserve chip width — one
          span, not a duplicate of every badge (KIR-2546). Hidden but laid out,
          so `offsetWidth` reads true. */}
      <span
        ref={registerChip}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 -z-10 inline-flex shrink-0 items-center whitespace-nowrap rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium opacity-0"
      >
        +{clients.length}
      </span>
    </div>
  );
}
